import {
  clearSessionStorageItem,
  getSessionStorageItem,
  setSessionStorageItem,
} from "@/lib/storage";

type AutoRedirect = {
  origin: string;
  timestamp: number;
};

const LAST_AUTO_REDIRECT_KEY = "lastAutoRedirect";
const LOOP_WINDOW_MS = 30_000;

/**
 * Remember that we auto-redirected the browser to a product, so that we can
 * detect the product bouncing the user straight back (redirect loop).
 */
export const recordAutoRedirect = (url: string) => {
  try {
    setSessionStorageItem<AutoRedirect>(LAST_AUTO_REDIRECT_KEY, {
      origin: new URL(url).origin,
      timestamp: Date.now(),
    });
  } catch {
    // Malformed url - nothing to record.
  }
};

/**
 * True when we auto-redirected to the same origin moments ago - i.e. the
 * product rejected the session (e.g. 401) and sent the user back to auth.
 */
export const isRedirectLoop = (url: string | null): boolean => {
  if (!url) {
    return false;
  }

  const lastRedirect = getSessionStorageItem<AutoRedirect>(LAST_AUTO_REDIRECT_KEY);
  if (!lastRedirect) {
    return false;
  }

  try {
    return (
      new URL(url).origin === lastRedirect.origin &&
      Date.now() - lastRedirect.timestamp < LOOP_WINDOW_MS
    );
  } catch {
    return false;
  }
};

const BLOCKED_REDIRECT_KEY = "blockedRedirectUrl";
const BLOCK_TTL_MS = 120_000;

type BlockedRedirect = {
  url: string;
  timestamp: number;
};

/** Remember a redirect target that was rejected by the product, so the UI can explain it. */
export const blockRedirect = (url: string) => {
  setSessionStorageItem<BlockedRedirect>(BLOCKED_REDIRECT_KEY, {
    url,
    timestamp: Date.now(),
  });
};

/**
 * The blocked redirect url, or undefined when nothing is blocked. A block older
 * than the TTL is discarded so a transient product outage cannot leave a
 * permanent "no access" state - the next visit retries the redirect.
 */
export const getBlockedRedirectUrl = () => {
  const blocked = getSessionStorageItem<BlockedRedirect | string>(BLOCKED_REDIRECT_KEY);
  if (!blocked) {
    return undefined;
  }

  // Legacy plain-string entries (pre-TTL format) carry no timestamp - discard them.
  if (typeof blocked === "string" || Date.now() - blocked.timestamp > BLOCK_TTL_MS) {
    clearSessionStorageItem(BLOCKED_REDIRECT_KEY);
    return undefined;
  }

  return blocked.url;
};

export const clearBlockedRedirect = () => {
  clearSessionStorageItem(BLOCKED_REDIRECT_KEY);
};

export const productLabelFromUrl = (url: string) => {
  if (url.includes("score.")) {
    return "SkillCheck";
  } else if (url.includes("app.")) {
    return "SkillPanel";
  } else {
    return "this product";
  }
};
